import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_PROJECT_SORT_ID,
  projectsListQuery,
  resolveProjectSort,
  type ProjectFilters,
  type ProjectSortKey,
  type ProjectType,
} from '@/entities/project';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import type { SortDirection } from '@/shared/ui';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';

const DEFAULT_FILTERS: ProjectFilters = {
  status: [],
  owner: '',
};

export function useProjectsList() {
  const workspaceId = useCurrentWorkspaceId();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, SEARCH_DEBOUNCE_MS);

  // sortId === null means "use BE default" — the chip shows inactive in that
  // case. Selecting an explicit choice activates the chip. The BE default
  // matches the team's chosen default so a null state and the default choice
  // render the same rows.
  const [sortId, setSortId] = useState<string | null>(DEFAULT_PROJECT_SORT_ID);
  const resolvedSort = resolveProjectSort(sortId);
  const sortBy: ProjectSortKey | undefined = resolvedSort?.sortBy;
  const sortOrder: 'ASC' | 'DESC' | undefined = resolvedSort?.sortOrder;
  const sortDirection: SortDirection = sortOrder === 'ASC' ? 'asc' : 'desc';

  const [filters, setFilters] = useState<ProjectFilters>(DEFAULT_FILTERS);
  const [selectedType, setSelectedType] = useState<ProjectType | null>(null);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  // D22 follow-up — routes through the entity's `projectsListQuery` queryOptions
  // helper so the cache key (workspaceId + query payload) is constructed by the
  // PROJECTS_QUERY_KEYS.list factory rather than an ad-hoc inline array.
  // This is an intentional cache-key SHAPE change (8-element ad-hoc → 4-element
  // factory tuple `['projects', 'list', wsId, query]`); workspace switching
  // still invalidates correctly because the wsId stays in the key. Acceptable
  // single-deploy break since no cross-deploy cache state exists.
  const query = useQuery(
    projectsListQuery(workspaceId, {
      sortBy,
      sortOrder,
      page: page + 1,
      pageSize: rowsPerPage,
      search: debouncedSearchInput || undefined,
      status: filters.status.length > 0 ? filters.status : undefined,
      ownerId: filters.owner.length > 0 ? filters.owner : undefined,
      type: selectedType ? [selectedType] : undefined,
    }),
  );

  const isLoading = query.isFetching;
  const rows = query.data?.items ?? [];
  const totalCount = query.data?.total ?? 0;

  const hasSearch = Boolean(debouncedSearchInput);
  const hasActiveFilters =
    filters.status.length > 0 || filters.owner.length > 0 || selectedType !== null;

  const handleFiltersChange = (newFilters: ProjectFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setSelectedType(null);
    setPage(0);
  };

  const handleTypeChange = (type: ProjectType | null) => {
    setSelectedType(type);
    setPage(0);
  };

  const handleSortChange = (next: string | null) => {
    // Reset → null clears the chip (BE default applies). Explicit choice → set.
    setSortId(next);
    setPage(0);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return {
    // Query state
    isLoading,
    isError: query.isError,
    rows,
    totalCount,
    hasSearch,
    hasActiveFilters,

    // Search
    searchInput,
    setSearchInput,

    // Sort
    sortId,
    sortBy,
    sortDirection,
    handleSortChange,

    // Filters
    filters,
    handleFiltersChange,
    handleClearFilters,

    // Type filter
    selectedType,
    handleTypeChange,

    // Pagination
    rowsPerPage,
    page,
    handleChangePage,
    handleChangeRowsPerPage,
  };
}
